import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../lib/LanguageContext";
import { CONTACT } from "../lib/translations";
import { Phone, Mail, MapPin, Download } from "lucide-react";

const Footer = () => {
  const { t, lang } = useLang();

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/facilities", label: t.nav.facilities },
    { to: "/activities", label: t.nav.activities },
    { to: "/gallery", label: t.nav.gallery },
    { to: "/achievements", label: t.nav.achievements },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <footer className="bg-school-primary text-white mt-20" data-testid="main-footer">
      {/* Top */}
      <div className="container-school py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div>
            <div className="font-heading font-extrabold text-lg leading-tight text-white">{t.schoolName}</div>
            <div className="text-sm text-school-secondary mt-1">{t.schoolTagline}</div>
          </div>
          <p className="mt-5 text-sm text-white/85 leading-relaxed">{t.footer.about}</p>
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            data-testid="footer-brochure-btn"
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-school-secondary text-[#2B2B2B] font-semibold text-sm hover:bg-[var(--school-secondary-dark)] transition-colors"
          >
            <Download className="w-4 h-4" />
            {t.footer.downloadBrochure}
          </a>
        </div>

        <div>
          <h4 className="font-heading font-bold text-base mb-5 text-school-secondary">{t.footer.quickLinks}</h4>
          <ul className="space-y-3">
            {links.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                  className="text-sm text-white/90 hover:text-school-secondary transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-heading font-bold text-base mb-5 text-school-secondary">{t.footer.contact}</h4>
          <ul className="space-y-4 text-sm">
            <li className="flex gap-3">
              <MapPin className="w-5 h-5 text-school-secondary shrink-0 mt-0.5" />
              <span className="text-white/90 leading-relaxed">{lang === "mr" ? CONTACT.addressMr : CONTACT.addressEn}</span>
            </li>
            <li className="flex gap-3">
              <Phone className="w-5 h-5 text-school-secondary shrink-0 mt-0.5" />
              <div className="flex flex-col gap-0.5">
                <a href={`tel:${CONTACT.phoneRaw}`} className="text-white/85 hover:text-school-secondary transition-colors font-heading font-semibold">
                  {lang === "mr" ? CONTACT.phonePrimary : CONTACT.phonePrimaryEn}
                </a>
                <a href={`tel:${CONTACT.phoneRawSecondary}`} className="text-white/85 hover:text-school-secondary transition-colors font-heading font-semibold">
                  {lang === "mr" ? CONTACT.phoneSecondary : CONTACT.phoneSecondaryEn}
                </a>
              </div>
            </li>
            <li className="flex gap-3">
              <Mail className="w-5 h-5 text-school-secondary shrink-0 mt-0.5" />
              <a href={`mailto:${CONTACT.email}`} className="text-white/90 hover:text-school-secondary transition-colors break-all">
                {CONTACT.email}
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading font-bold text-base mb-5 text-school-secondary">{t.vision}</h4>
          <p className="text-sm text-white/90 leading-relaxed">
            {lang === "mr"
              ? "आमच्या शाळेच्या प्रगतीचा प्रवास तुमच्या मुलासोबत सुरू करा. आजच प्रवेशासाठी चौकशी करा."
              : "Begin your child's journey with our growing family. Enquire for admission today."}
          </p>
          <Link
            to="/contact#enquiry"
            data-testid="footer-enquire-btn"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-school-accent text-white font-semibold text-sm hover:bg-[var(--school-accent-dark)] transition-colors"
          >
            {t.nav.enquire}
          </Link>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/10">
        <div className="container-school py-5 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-white/75">
          <div>© {new Date().getFullYear()} {t.schoolName}. {t.footer.rights}.</div>
          <div className="flex items-center gap-3">
            <span className="text-white/65">
              {lang === "mr" ? "Designed and Developed by " : "Designed and Developed by "}
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                data-testid="footer-credit"
                className="font-heading font-semibold text-white/85 hover:text-school-secondary transition-colors"
              >
                SM Computers
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
